/*
 * 还款计划管理页面 (独立一级页面，菜单入口)
 *
 * 本页面是按揭贷款模块的"还款计划"管理页面，以 Tab 形式展示三种贷款类型的还款计划：
 *   Tab1 - 信贷客户 (loanType=MORTGAGE)
 *   Tab2 - 信用卡客户 (loanType=CARD)
 *   Tab3 - 个人借款 (loanType=BORROW)
 *
 * 每个Tab独立使用 Crud 组件，共享 entityKey="repaymentPlan"，通过 params.loanType 区分数据。
 *
 * === API调用链路 ===
 *
 * 1. 列表查询 (每个Tab各自请求)
 *    GET /repaymentPlan?page=&size=&loanType=MORTGAGE/CARD/BORROW&...筛选参数
 *    → RepaymentPlanController.list (menuType=HKJH)
 *    → RepaymentPlanServiceImpl.queryRepaymentPlanPage
 *    → RepaymentPlanMapper.selectRepaymentPlanPage
 *    ← Page<RepaymentPlan> { current, total, size, records }
 *
 * 2. 统计汇总 (每次列表加载后自动触发，fetchDataAfterMethod)
 *    GET /repaymentPlan/listSum?loanType=MORTGAGE/CARD/BORROW&...筛选参数
 *    → RepaymentPlanController.listSum
 *    → RepaymentPlanServiceImpl.queryRepaymentPlanListSum
 *    → RepaymentPlanMapper.selectRepaymentPlanListSum
 *    ← 各Tab统计字段不同:
 *       信贷: amount, loanHelpRepaymentPlanAmount, loanRepaymentPlanAmount, borrowReturnAmount,
 *             oneTermAmount, dayMortgageOneTermAmount, dayMortgagePaidAmount,
 *             monthMortgageOneTermAmount, monthMortgagePaidAmount
 *       信用卡: amount, postingAmount, cardHelpRepaymentPlanAmount, oneTermAmount,
 *              dayMortgageOneTermAmount, dayMortgagePaidAmount,
 *              monthMortgageOneTermAmount, monthMortgagePaidAmount
 *       借款: borrowAmount, borrowReturnAmount, dayBorrowOneTermAmount, dayBorrowPaidAmount,
 *             monthBorrowOneTermAmount, monthBorrowPaidAmount
 *
 * 3. 批量还款/结清/过账
 *    POST /repaymentPlan/doRepayment
 *    → RepaymentPlanController.doRepayment
 *    → RepaymentPlanServiceImpl.doRepayment (批量遍历每个计划)
 *      → 校验(金额>0、出款人、日期不为空)
 *      → 根据 paidOptType(HK/JQ/GZ) + loanType + paidMethod 组合确定最终 paidMethod
 *      → repaymentPlanMapper.updateById (更新 status, paidAmount, paidDate, paidUserId, paidMethod, paidDays, imageUrls)
 *      → 如果信用卡结清且 isTransform=true: 更新 mortgage_loan.loan_type=CARD→MORTGAGE
 *    ← { code: "ok" }
 *
 * 4. 删除还款计划
 *    POST /repaymentPlan/delete
 *    → RepaymentPlanController.delete → RepaymentPlanServiceImpl.delete
 *    ← 仅允许删除手动创建的计划(createdBy>0)，且不能删除已还款的
 *
 * 5. 取消还款
 *    POST /repaymentPlan/cancelRepayment
 *    → RepaymentPlanController.cancelRepayment → RepaymentPlanServiceImpl.cancelRepayment
 *    ← 清空 paidAmount, paidDate, paidUserId, paidUserName, paidMethod, paidDays
 *
 * 6. 发送还款提醒
 *    POST /repaymentPlan/batchCreatePayLoanTodo
 *    → RepaymentPlanController.batchCreatePayLoanTodo
 *    → RepaymentPlanServiceImpl.batchCreatePayLoanTodo
 *    ← 为选中的还款计划创建待办提醒
 *
 * 7. 查询贷款所有还款计划 (弹窗中点击行时触发)
 *    GET /repaymentPlan/selectRepaymentPlanList?loanId=
 *    → RepaymentPlanController.selectRepaymentPlanList
 *    → RepaymentPlanServiceImpl.selectRepaymentPlanListByLoanId
 *    ← List<RepaymentPlan>
 *
 * 8. 获取出款人列表
 *    apiClient.getTeamMemberByDataScopeAsOpts("payment")
 *    → 根据当前用户的数据权限返回可选的出款人列表(团队成员)
 *
 * 9. 图片上传
 *    POST /attachment/upload
 *    → AttachmentController.upload
 *    ← 图片URL，存入 repayment_plan.imageUrls 字段
 *
 * === 批量操作按钮 ===
 *   还款 (HK)   - 标记还款，paidOptType="HK"
 *   结清 (JQ)   - 标记结清，paidOptType="JQ"，信用卡结清时可选择转为信贷客户
 *   过账 (GZ)   - 标记过账，paidOptType="GZ"，仅信用卡Tab可见
 *   提醒        - 发送还款提醒待办
 *   取消还款     - 清空还款记录
 *   删除        - 删除手动创建的还款计划
 *
 * === 还款弹窗结构 ===
 *   左上(span=18): 已选计划表格(带汇总行) + 右侧(span=6): 还款信息表单
 *     - 还款方式: 代还(选择出款人) / 客户自还(默认客户姓名)
 *     - 还款金额、实际还款日期、还款图片
 *   左下(span=18): 该贷款所有还款计划表格(带汇总行)
 */

import { Col, ColType, Crud, CrudAction, CrudApi } from "../../comp/crud";
import {
  ApiClient,
  displayPaidMethod,
  getPaidMethodOpt,
  displayMortgageLoanType,
  displayRepaymentPlanStatus,
  getLoanTypeItemOpt,
  getRepaymentPlanStatusOpt,
  RepaymentPlanStatus,
  getEndpointUrl,
} from "../../api/ApiClient";
import { FileItem } from "@douyinfe/semi-ui/lib/es/upload";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Form,
  Modal,
  Typography,
  Toast,
  InputNumber,
  DatePicker,
  Tag,
  TabPane,
  Tabs,
  Button,
  Descriptions,
  Row,
  Upload,
  Table,
  Image,
  Popover,
  Tooltip,
  Card,
  Badge
} from "@douyinfe/semi-ui";
import { FormApi } from "@douyinfe/semi-ui/lib/es/form";
import { formatAmountZhCn, formatDate, renderStatDetailContentPlan, renderRepaymentPlanStatusTag, renderPaidMethodTag, renderPlatformTooltip, renderPeriodNo, renderReviewStatusTag, renderStatusTransition, ConfirmStatusOptions } from "../../utils";
import {
  IconCoinMoneyStroked,
  IconHelpCircle,
  IconIdCard,
  IconModalStroked,
  IconMoneyExchangeStroked,
  IconPlus,
  IconBarChartHStroked,
  IconCalendarClock,
  IconPlusCircle,
  IconMinusCircle,
} from "@douyinfe/semi-icons";
import { Col as RowCol } from "@douyinfe/semi-ui/lib/es/grid";
import dayjs from "dayjs";

let apiClient = ApiClient.getDefault();
const { Text } = Typography;

/**
 * 日期快捷筛选工具函数(独立于组件，避免随父组件re-render重建)
 */
const applyDueDateFilter = (apiRef: React.MutableRefObject<CrudApi | undefined>, formRef: React.MutableRefObject<any>, startDate: Date, endDate: Date) => {
  const api = apiRef.current;
  if (!api) { Toast.info('页面加载中，请稍后再试'); return; }
  const formApi = formRef.current;
  if (!formApi) { Toast.info('页面加载中，请稍后再试'); return; }
  formApi.setValue('dueDate', [startDate, endDate]);
  api.refresh();
};

/**
 * 日历快捷按钮 + 日历视图组件(独立于父组件，避免随父组件re-render而卸载重建导致calendarCount重复调用)
 */
const DueDateQuickButtons = ({ apiRef, formRef, loanType, refreshKey, active }: { apiRef: React.MutableRefObject<CrudApi | undefined>; formRef: React.MutableRefObject<any>; loanType: string; refreshKey?: number; active?: boolean }) => {
  const today = new Date();
  const [showCalendar, setShowCalendar] = useState(true);
  const [calendarMonth, setCalendarMonth] = useState(dayjs());
  const [calendarData, setCalendarData] = useState<Record<string, any>>({});
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const loadCalendarData = useCallback(() => {
    const start = calendarMonth.startOf('month').format('YYYY-MM-DD');
    const end = calendarMonth.endOf('month').format('YYYY-MM-DD');
    apiClient.request('/repaymentPlan/calendarCount', 'GET', {
      loanType, startDate: start, endDate: end,
    }).then((resp: any) => {
      if (resp.code === 'ok' && resp.data) {
        const map: Record<string, any> = {};
        (resp.data as any[]).forEach((item: any) => { map[item.dueDate] = item; });
        setCalendarData(map);
      }
    });
  }, [calendarMonth, loanType]);

  useEffect(() => { if (showCalendar && active !== false) loadCalendarData(); }, [showCalendar, loadCalendarData, refreshKey, active]);

  const handleDateClick = (dateStr: string) => {
    const api = apiRef.current;
    const formApi = formRef.current;
    if (!api || !formApi) return;
    if (selectedDate === dateStr) {
      setSelectedDate(null);
      formApi.setValue('dueDate', undefined);
    } else {
      setSelectedDate(dateStr);
      const d = new Date(dateStr);
      formApi.setValue('dueDate', [d, d]);
    }
    api.refresh();
  };

  const clearDateFilter = () => {
    setSelectedDate(null);
    const api = apiRef.current;
    const formApi = formRef.current;
    if (api && formApi) {
      formApi.setValue('dueDate', undefined);
      api.refresh();
    }
  };

  const nearDays = [
    { label: '近3天', days: 3 },
    { label: '近7天', days: 7 },
    { label: '近15天', days: 15 },
  ];
  const months = Array.from({ length: 5 }, (_, i) => {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
    return {
      label: `${d.getMonth() + 1}月`,
      start: new Date(d.getFullYear(), d.getMonth(), 1),
      end: new Date(d.getFullYear(), d.getMonth() + 1, 0),
    };
  });

  // Calendar rendering
  const weekDays = ['一', '二', '三', '四', '五', '六', '日'];
  const monthStart = calendarMonth.startOf('month');
  const monthEnd = calendarMonth.endOf('month');
  let startDay: number = monthStart.day(); // 0=Sun
  startDay = startDay === 0 ? 7 : startDay - 1; // convert to Mon=0
  const daysInMonth = calendarMonth.daysInMonth();

  const cells: (number | null)[] = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const cellStyle: React.CSSProperties = {
    width: 'calc(100%/7)', textAlign: 'center', cursor: 'pointer',
    borderRadius: 4, transition: 'background 0.2s', minHeight: 48,
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  };
  const barBg: React.CSSProperties = { width: '100px', height: 4, borderRadius: 2, background: 'var(--semi-color-fill-2)', };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', margin: '4px 0 2px', flexWrap: 'wrap' }}>
        <Tooltip content={"点击打开或关闭日历,"}>
        <Button
          size="small"
          theme={showCalendar ? 'solid' : 'outline'}
          type="warning"
          icon={<IconCalendarClock />}
          onClick={() => setShowCalendar(!showCalendar)}
          style={{ fontWeight: 600 }}
        >
          日历视角
        </Button>
        </Tooltip>
        <span style={{ color: 'var(--semi-color-text-2)', fontSize: 12, fontWeight: 500 }}>还款日期:</span>
        {nearDays.map(item => {
          const s = new Date(today); s.setDate(s.getDate() - item.days);
          const e = new Date(today); e.setDate(e.getDate() + item.days);
          return <Button key={item.label} size="small" theme="light" onClick={() => applyDueDateFilter(apiRef, formRef, s, e)}>{item.label}</Button>;
        })}
        <span style={{ color: '#ddd', margin: '0 2px' }}>|</span>
        {months.map(item => (
          <Button key={item.label} size="small" theme="light" onClick={() => applyDueDateFilter(apiRef, formRef, item.start, item.end)}>{item.label}</Button>
        ))}
      </div>

      {showCalendar && (
        <Card style={{ margin: '4px 0', background: 'var(--semi-color-bg-2)' }} bodyStyle={{ padding: '8px 12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <Button icon={<IconMinusCircle />} size="small" theme="light"
              onClick={() => { setCalendarMonth(calendarMonth.subtract(1, 'month')); setSelectedDate(null); }}
            />
            <Text strong style={{ fontSize: 15 }}>{calendarMonth.format('YYYY年M月')}</Text>
            <div style={{ display: 'flex', gap: 6 }}>
              {selectedDate && <Button size="small" type="tertiary" onClick={clearDateFilter}>清除筛选</Button>}
              <Button icon={<IconPlusCircle />} size="small" theme="light"
                onClick={() => { setCalendarMonth(calendarMonth.add(1, 'month')); setSelectedDate(null); }}
              />
            </div>
          </div>
          <div style={{ display: 'flex' }}>
            {weekDays.map(w => (
              <div key={w} style={{ width: 'calc(100%/7)', textAlign: 'center', fontSize: 15, color: 'var(--semi-color-text-2)', fontWeight: 600, padding: '4px 20px' }}>{w}</div>
            ))}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {cells.map((day, i) => {
              if (day === null) return <div key={`e${i}`} style={cellStyle} />;
              const dateStr = calendarMonth.date(day).format('YYYY-MM-DD');
              const stat = calendarData[dateStr];
              const isSelected = selectedDate === dateStr;
              const isToday = dayjs().format('YYYY-MM-DD') === dateStr;
              const totalCount = stat?.totalCount || 0;
              const totalAmount = stat?.totalAmount || 0;
              const doneCount = stat?.doneCount || 0;
              const doneAmount = stat?.doneAmount || 0;
              const undoneCount = stat?.undoneCount || 0;
              const undoneAmount = stat?.undoneAmount || 0;
              const pct = totalCount > 0 ? Math.round(doneCount / totalCount * 100) : -1;
              let bgColor = 'transparent';
              if (isSelected) bgColor = 'var(--semi-color-primary-light-default)';
              else if (isToday) bgColor = 'var(--semi-color-tertiary-light-default)';
              return (
                <div key={dateStr}
                  style={{ ...cellStyle, background: bgColor }}
                  onClick={() => totalCount > 0 && handleDateClick(dateStr)}
                >
                  <span style={{ fontSize: 14, fontWeight: isToday ? 700 : 400, color: isToday ? 'var(--semi-color-primary)' : 'var(--semi-color-text-0)' }}>{day}</span>
                  {totalCount > 0 ? (
                    <>
                      {/* <span style={{ fontSize: 14, color: undoneAmount > 0 ? '#e91e63' : '#52c41a', fontWeight: 600 }}>{doneAmount}/{totalAmount}</span> */}
                      <span style={{ fontSize: 14, color: undoneCount > 0 ? '#e91e63' : '#52c41a', fontWeight: 600 }}>{doneCount}/{totalCount}</span>
                      <div style={barBg}>
                        <div style={{ width: `${pct}%`, height: '100%', borderRadius: 2, background: undoneCount > 0 ? '#e91e63' : '#52c41a' }} />
                      </div>
                    </>
                  ) : <span style={{ fontSize: 14, color: 'var(--semi-color-text-2)' }}>-</span>}
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
};

/**
 * 确认状态筛选按钮组件(独立于父组件，避免随父组件re-render而卸载重建)
 */
const ConfirmStatusFilterButtons = ({ formRef, confirmStatusCounts, currentTab, crudApi, crudApiCard, crudApiBorrow }: {
  formRef: React.MutableRefObject<any>
  confirmStatusCounts: { PENDING: number; REJECTED: number; CANCEL: number; EDIT: number }
  currentTab: string
  crudApi?: CrudApi
  crudApiCard?: CrudApi
  crudApiBorrow?: CrudApi
}) => {
  const [activeFilter, setActiveFilter] = useState<string>("");
  const handleFilterClick = (status: string) => {
    const newStatus = activeFilter === status ? "" : status;
    setActiveFilter(newStatus);
    const formApi = formRef.current;
    if (formApi) {
      formApi.setValue('status', newStatus || undefined);
    }
    // 刷新当前Tab的Crud
    if (currentTab == "1") crudApi?.refresh();
    else if (currentTab == "2") crudApiCard?.refresh();
    else crudApiBorrow?.refresh();
  };
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '4px 0 6px', flexWrap: 'wrap' }}>
      <span style={{ color: 'var(--semi-color-text-2)', fontSize: 12, fontWeight: 500 }}>确认状态:</span>
      <Button
        size="small"
        theme={activeFilter === 'PENDING' ? 'solid' : 'light'}
        type="warning"
        onClick={() => handleFilterClick('PENDING')}
      >
        待审核 {<Badge type='danger' count={confirmStatusCounts.PENDING?confirmStatusCounts.PENDING:0} style={{ marginLeft: 4 }} /> }
      </Button>
      <Button
        size="small"
        theme={activeFilter === 'REJECTED' ? 'solid' : 'light'}
        type="danger"
        onClick={() => handleFilterClick('REJECTED')}
      >
        已驳回 {<Badge type='danger' count={confirmStatusCounts.REJECTED?confirmStatusCounts.REJECTED:0} style={{ marginLeft: 4 }} /> }
      </Button>
      <Button
        size="small"
        theme={activeFilter === 'CANCEL' ? 'solid' : 'light'}
        type="secondary"
        onClick={() => handleFilterClick('CANCEL')}
      >
        取消中 {<Badge type='danger' count={confirmStatusCounts.CANCEL?confirmStatusCounts.CANCEL:0} style={{ marginLeft: 4 }} /> }
      </Button>
      <Button
        size="small"
        theme={activeFilter === 'EDIT' ? 'solid' : 'light'}
        type="primary"
        onClick={() => handleFilterClick('EDIT')}
      >
        编辑中 {<Badge type='danger' count={confirmStatusCounts.EDIT?confirmStatusCounts.EDIT:0} style={{ marginLeft: 4 }} /> }
      </Button>
    </div>
  );
};

export interface RepaymentPlanProps {
  loanId?: number;
}

export default function IndexRepaymentPlan(props: RepaymentPlanProps) {
  // 三个Tab各自独立的 CrudApi 引用，用于刷新对应Tab的数据
  const [crudApi, setCrudApi] = useState(undefined as undefined | CrudApi);           // Tab1 信贷
  const [crudApiCard, setCrudApiCard] = useState(undefined as undefined | CrudApi);    // Tab2 信用卡
  const [crudApiBorrow, setCrudApiBorrow] = useState(undefined as undefined | CrudApi);// Tab3 个人借款

  // 还款弹窗状态
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [modifyFormApi, setModifyFormApi] = useState(undefined as undefined | FormApi);
  // 当前选中的计划(右侧表单数据源)
  const [selectedPlan, setSelectedPlan] = useState({} as any);
  // 批量选中的计划列表(弹窗左上表格数据源)
  const [selectedPlanList, setSelectedPlanList] = useState([] as any);
  // 当前选中计划所属贷款的所有还款计划(弹窗左下表格数据源)
  const [selectedLoanAllPlanList, setSelectedLoanAllPlanList] = useState([] as any);

  const [modifySaving, setModifySaving] = useState(false);
  // 出款人下拉选项(团队成员树)
  const [teamMembersTree, setTeamMembersTree] = useState([] as any[]);
  // 弹窗标题: "批量还款" / "批量结清" / "批量过账"
  const [modalTitle, setModalTitle] = useState("还款");
  const [savingTodo, setSavingTodo] = useState(false);

  // 平台链接弹窗 - 贷款汇总 & 全部还款计划
  const [showLoanDetailModal, setShowLoanDetailModal] = useState(false);
  const [loanDetailRecord, setLoanDetailRecord] = useState<any>(null);
  const [loanDetailPlans, setLoanDetailPlans] = useState<any[]>([]);

  // ====== Tab1 信贷统计 ======
  const [statData, setStatData] = useState([] as any[]);          // 格式化后的统计展示数据
  const [statDataObj, setStatDataObj] = useState({} as any);      // 后端返回的原始统计对象
  // ====== Tab2 信用卡统计 ======
  const [statDataCard, setStatDataCard] = useState([] as any[]);
  const [statDataObjCard, setStatDataObjCard] = useState({} as any);
  // ====== Tab3 个人借款统计 ======
  const [statDataBorrow, setStatDataBorrow] = useState([] as any[]);
  const [statDataObjBorrow, setStatDataObjBorrow] = useState({} as any);

  // 当前Tab显示的批量操作按钮(根据Tab动态过滤，信用卡Tab显示"过账"按钮)
  const [batchActions, setBatchActions] = useState([] as any[]);
  // 日历刷新key，操作还款/过账/结清/取消还款后递增以触发日历数据刷新
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
  // 还款方式: "代还" | "客户自还" (弹窗右侧表单)
  const [payType, setPayType] = useState(undefined as string | undefined);
  // 贷款类型: "MORTGAGE" | "BORROW" (弹窗中控制表单字段)
  const [loanType, setLoanType] = useState(undefined as string | undefined);
  const username = localStorage.getItem("username");
  const role = localStorage.getItem("role");
  // 当前激活的Tab索引(响应式state，跨渲染保持)
  const [currentTab, setCurrentTab] = useState("1");

  // ====== 审批流日志弹窗状态 ======
  const [confirmLogModalVisible, setConfirmLogModalVisible] = useState(false);
  const [confirmLogRecord, setConfirmLogRecord] = useState<any>(null);
  const [confirmLogs, setConfirmLogs] = useState<any[]>([]);

  // ====== 确认状态筛选 ======
  const [confirmStatusCounts, setConfirmStatusCounts] = useState<any>({ PENDING: 0, REJECTED: 0, CANCEL: 0, EDIT: 0 });
  const [confirmStatusCountsCard, setConfirmStatusCountsCard] = useState<any>({ PENDING: 0, REJECTED: 0, CANCEL: 0, EDIT: 0 });
  const [confirmStatusCountsBorrow, setConfirmStatusCountsBorrow] = useState<any>({ PENDING: 0, REJECTED: 0, CANCEL: 0, EDIT: 0 });


  const calcConfirmStatusCounts = (data: any[]) => {
    const counts = { PENDING: 0, REJECTED: 0, CANCEL: 0, EDIT: 0 };
    if (!data) return counts;
    data.forEach((item: any) => {
      if (item.status === 'PENDING') counts.PENDING++;
      else if (item.status === 'REJECTED') counts.REJECTED++;
      else if (item.status === 'CANCEL') counts.CANCEL++;
      else if (item.status === 'EDIT') counts.EDIT++;
    });
    return counts;
  };

  /**
   * 显示确认日志弹窗
   * 点击确认状态Tag触发，查询该还款计划的确认日志列表
   * 调用链: GET /repaymentPlan/confirmLogs?repaymentId=
   */
  const showConfirmLogModal = (record: any) => {
    setConfirmLogRecord(record);
    setConfirmLogs([]);
    setConfirmLogModalVisible(true);
    apiClient
      .request("/repaymentPlan/confirmLogs", "GET", { repaymentPlanId: record.id })
      .then((resp: any) => {
        setConfirmLogs(resp.data || []);
      });
  };

  // ====== Tab1 信贷/Tab2信用卡 列定义(通用) ======
  // 列定义说明:
  //   showInFilter=true → 出现在筛选面板
  //   hideInCreate/hideInEdit=true → 不出现在新建/编辑弹窗(本页面不使用Crud自带弹窗)
  //   render → 自定义表格列渲染(状态Tag、金额格式化、客户姓名链接等)
  let cols: Col[] = [
    {
      name: "ID",
      key: "id",
      hideInEdit: true,
      hideInList: true,
      type: ColType.NUMBER,
    },
    {
      name: "贷款ID",
      key: "loanId",
      hideInEdit: true,
      hideInList: true,
      showInFilter: false,
      type: ColType.NUMBER,
    },

    {
      name: "客户ID",
      key: "leadId",
      width: 80,
      hideInEdit: true,
      hideInList: true,
      showInFilter: true,       // 筛选面板中显示
      type: ColType.NUMBER,
    },
    {
      name: "客户姓名",
      key: "leadName",
      width: 80,
      hideInEdit: true,
      hideInList: false,
      showInFilter: true,
      type: ColType.STRING,
      // 点击客户姓名 → 打开客户详情页(新窗口)
      render: (value, record, index) => {
        return (
          <Typography.Text
                        link           
            onClick={() => openMortgageLoanModal(record.leadId,record.leadName)}
          >
            {value}({record.leadId})
          </Typography.Text>
        );
      },
    },
    {
      name: "城市",
      key: "leadCity",
      width: 50,
      hideInEdit: true,
      hideInList: false,
      showInFilter: true,
      type: ColType.STRING,
    },

    {
      name: "按揭专员ID",
      key: "agentId",
      type: ColType.STRING,
      showInFilter: false,
      hideInList: true,
      hideInCreate: true,
      hideInEdit: true,
    },
    {
      name: "按揭专员",
      key: "agentName",
      width: 80,
      type: ColType.STRING,
      showInFilter: true,
      hideInCreate: true,
      hideInEdit: true,
    },
    {
      name: "销售ID",
      key: "saleId",
      type: ColType.STRING,
      showInFilter: false,
      hideInList: true,
      hideInCreate: true,
      hideInEdit: true,
    },
    {
      name: "销售",
      key: "saleName",
      width: 80,
      type: ColType.STRING,
      showInFilter: true,
      hideInCreate: true,
      hideInEdit: true,
    },
    {
      name: "贷款类型",
      key: "loanType",
      width: 100,
      type: ColType.SELECT,
      hideInList: true,
      render: (v, i) => displayMortgageLoanType(v),
      getOptions: () => Promise.resolve(getLoanTypeItemOpt()),
    },
    {
      name: "贷款平台",
      key: "platform",
      width: 100,
      type: ColType.STRING,
      showInFilter: true,
      render: (text: any, record: any) => (
        <Tooltip style={{maxWidth:350}} content={renderPlatformTooltip(record)}>
          <a href="javascript:void(0)" onClick={() => openLoanDetail(record)} style={{ color: "#1677ff", textDecoration: "underline" }}>
            {text || "-"}
          </a>
        </Tooltip>
      ),
    },
    // {
    //   name: "贷款金额",
    //   key: "amount",
    //   width: 70,
    //   type: ColType.NUMBER,
    //   render: (a) => formatAmountZhCn(a),
    // },
    {
      name: "期数",
      key: "periodNo",
      width: 70,
      type: ColType.STRING,
      // 期数渲染: 手动创建的(蓝Tag)、客户自还的(橙Tag)、系统生成的(纯文本)
      render: renderPeriodNo
    },
    {
      name: "剩余还款金额",
      key: "remainAmount",
      type: ColType.NUMBER,
      hideInList: true,
      render: (a) => formatAmountZhCn(a),
    },
    {
      name: "月供金额",
      key: "oneTermAmount",
      width: 70,
      type: ColType.NUMBER,
      render: (a) => formatAmountZhCn(a),
    },
    {
      name: "还款日期",
      key: "dueDate",
      width: 90,
      type: ColType.DATE_RANGE,     // 日期范围筛选，筛选时转为 "startDate,endDate"
      showInFilter: true,
      defaultSortOrder: false,
    },

    {
      name: "状态",
      key: "status",
      width: 70,
      type: ColType.SELECT,
      showInFilter: true,
      hideInEdit: true,
      hideInCreate: true,
      render: (value, record, index) => (
        <span onClick={() => showConfirmLogModal(record)} style={{ cursor: 'pointer' }}>
          {renderRepaymentPlanStatusTag(value, record)}
        </span>
      ),
      getOptions: (row) => Promise.resolve(getRepaymentPlanStatusOpt()),
    },

    {
      name: "出款金额",
      key: "paidAmount",
      width: 70,
      type: ColType.STRING,
      hideInCreate: true,
      render: (a) => formatAmountZhCn(a),
    },
    {
      name: "出款人",
      key: "paidUserName",
      width: 80,
      type: ColType.STRING,
      hideInCreate: true,
      showInFilter: true,
    },
    {
      name: "出款日期",
      key: "paidDate",
      width: 90,
      type: ColType.DATE_RANGE,
      hideInCreate: true,
      showInFilter: true,
      defaultSortOrder: false,
    },
    {
      name: "还款方式",
      key: "paidMethod",
      width: 100,
      type: ColType.SELECT,
      showInFilter: true,
      hideInEdit: true,
      hideInCreate: true,
      getOptions: (row) => Promise.resolve(getPaidMethodOpt()),
      render: (value) => renderPaidMethodTag(value),
    },
    {
      name: "还款图片",
      key: "imageUrls",
      width: 40,
      type: ColType.STRING,
      hideInCreate: true,
      // 图片预览: 显示第一张缩略图，点击可预览所有图片
      render: (value, record, index) => {
        if(value){
           return (<Image
                    width={50}
                    height={50}
                    src={record.imageUrls && record.imageUrls.length > 0 ? record.imageUrls[0].url : ''}
                    preview={{ src: (record.imageUrls || []).map(item => item.url) }}
                  />);
        } else {
          return '';
        }
      }
    },

    
    {
      name: "还款天数",
      key: "paidDays",
      width: 40,
      type: ColType.NUMBER,
      hideInCreate: true,
    },

    {
      name: "创建时间",
      key: "createdAt",
      width: 150,
      type: ColType.DATETIME,
      hideInEdit: true,
      hideInCreate: true,
    },
  ];

  // ====== Tab3 个人借款 列定义 ======
  // 与 cols 的区别: 出款人字段改为 borrowerName(出借人), 增加 出借日期, 无 过账金额/账单日
  let borrowCols: Col[] = [
    {
      name: "ID",
      key: "id",
      hideInEdit: true,
      hideInList: true,
      type: ColType.NUMBER,
    },
    {
      name: "贷款ID",
      key: "loanId",
      hideInEdit: true,
      hideInList: true,
      showInFilter: false,
      type: ColType.NUMBER,
    },

    {
      name: "客户ID",
      key: "leadId",
      width: 80,
      hideInEdit: true,
      hideInList: true,
      showInFilter: true,
      type: ColType.NUMBER,
    },
    {
      name: "客户姓名",
      key: "leadName",
      width: 80,
      hideInEdit: true,
      hideInList: false,
      showInFilter: true,
      type: ColType.STRING,
      render: (value, record, index) => {
        return (
          <Typography.Text
                        link           
            onClick={() => openMortgageLoanModal(record.leadId,record.leadName)}
          >
            {value}({record.leadId})
          </Typography.Text>
        );
      },
    },
    {
      name: "城市",
      key: "leadCity",
      width: 50,
      hideInEdit: true,
      hideInList: false,
      showInFilter: true,
      type: ColType.STRING,
    },
    {
      name: "贷款平台",
      key: "platform",
      width: 100,
      type: ColType.STRING,
      showInFilter: true,
      render: (text: any, record: any) => (
        <Tooltip style={{maxWidth:350}} content={renderPlatformTooltip(record)}>
          <a href="javascript:void(0)" onClick={() => openLoanDetail(record)} style={{ color: "#1677ff", textDecoration: "underline" }}>
            {text || "-"}
          </a>
        </Tooltip>
      ),
    },
    {
      name: "按揭专员ID",
      key: "agentId",
      type: ColType.STRING,
      showInFilter: false,
      hideInList: true,
      hideInCreate: true,
      hideInEdit: true,
    },
    {
      name: "按揭专员",
      key: "agentName",
      width: 80,
      type: ColType.STRING,
      showInFilter: true,
      hideInCreate: true,
      hideInEdit: true,
    },
    {
      name: "销售ID",
      key: "saleId",
      type: ColType.STRING,
      showInFilter: false,
      hideInList: true,
      hideInCreate: true,
      hideInEdit: true,
    },
    {
      name: "销售",
      key: "saleName",
      width: 80,
      type: ColType.STRING,
      showInFilter: true,
      hideInCreate: true,
      hideInEdit: true,
    },
    {
      name: "出款人",          // 借款场景: 出款人 = 出借人(borrowerName)
      key: "borrowerName",
      width: 80,
      type: ColType.STRING,
      showInFilter: true,
      hideInCreate: true,
      hideInEdit: true,
    },

    {
      name: "出借金额",        // 借款场景: 贷款金额 = 出借金额
      key: "amount",
      width: 70,
      type: ColType.NUMBER,
      render: (a) => formatAmountZhCn(a),
    },
    {
      name: "剩余还款金额",
      key: "remainAmount",
      type: ColType.NUMBER,
      hideInList: true,
      render: (a) => formatAmountZhCn(a),
    },
    {
      name: "还款日期",
      key: "dueDate",
      width: 90,
      type: ColType.DATE_RANGE,
      showInFilter: true,
      defaultSortOrder: false,
    },
    {
      name: "状态",
      key: "status",
      width: 70,
      type: ColType.SELECT,
      showInFilter: true,
      hideInEdit: true,
      hideInCreate: true,
      render: (value, record, index) => (
        <span onClick={() => showConfirmLogModal(record)} style={{ cursor: 'pointer' }}>
          {renderRepaymentPlanStatusTag(value, record)}
        </span>
      ),
      getOptions: (row) => Promise.resolve(getRepaymentPlanStatusOpt()),
    },
    {
      name: "实还金额",
      key: "paidAmount",
      width: 70,
      type: ColType.STRING,
      hideInCreate: true,
      render: (a) => formatAmountZhCn(a),
    },
    {
      name: "收款人",          // 借款场景: 出款人 = 收款人(paidUserName)
      key: "paidUserName",
      width: 80,
      type: ColType.STRING,
      hideInCreate: true,
      showInFilter: true,
    },
    {
      name: "实还日期",
      key: "paidDate",
      width: 90,
      type: ColType.DATE,
      hideInCreate: true,
      showInFilter: true,
      defaultSortOrder: false,
    },
    {
      name: "还款图片",
      key: "imageUrls",
      width: 40,
      type: ColType.STRING,
      hideInCreate: true,
      render: (value, record, index) => {
        if(value){
           return (<Image
                    width={50}
                    height={50}
                    src={record.imageUrls && record.imageUrls.length > 0 ? record.imageUrls[0].url : ''}
                    preview={{ src: (record.imageUrls || []).map(item => item.url) }}
                  />);
        } else {
          return '';
        }
      }
    },
    {
      name: "用款天数",
      key: "paidDays",
      width: 40,
      type: ColType.NUMBER,
      hideInCreate: true,
    },

    {
      name: "出借日期",
      key: "borrowDate",
      width: 90,
      type: ColType.DATE,
      hideInEdit: true,
      hideInCreate: true,
    },
    {
      name: "创建时间",
      key: "createdAt",
      width: 150,
      type: ColType.DATETIME,
      hideInEdit: true,
      hideInCreate: true,
    },
  ];

  // ====== Tab2 信用卡 额外列 ======
  // 信用卡在通用 cols 基础上增加 "账单日" 和 "过账金额" 两列
  let colBillDay: Col = {
      name: "账单日",
      key: "billDay",
      width: 70,
      type: ColType.NUMBER,
      showInFilter: false,
      defaultSortOrder: false,
  };

  let colPostingAmount: Col = {
      name: "过账金额",
      key: "postingAmount",
      width: 70,
      type: ColType.STRING,
      hideInCreate: true,
      render: (a) => formatAmountZhCn(a),
  };

  // 深拷贝 cols 并在指定位置插入信用卡专属列
  let cardCols: Col[] = [...cols];
  cardCols.splice(15, 0, colBillDay);       // 在"月供金额"后插入"账单日"
  cardCols.splice(20, 0, colPostingAmount);  // 在"实际还款金额"后插入"过账金额"

  // 页面初始化: 加载出款人下拉选项(团队成员)
  useEffect(() => {
    let currUsername;
    if (role == "op_manager" || role == "monitor") {
      currUsername = "root";
    } else {
      currUsername = username;
    }
    // 获取所有有"payment"数据权限的团队成员作为出款人选项
    apiClient
      .getTeamMemberByDataScopeAsOpts("payment")
      .then((opts) => setTeamMembersTree(opts));

    return () => {};
  }, []);

  // 出款人下拉选项的渲染标签(显示城市和团队信息)
  const renderLabel = (label: any, item: any) => (
    <div style={{ padding: "6px 8px", lineHeight: 1.0 }}>
      <div style={{ fontWeight: 500, color: "var(--semi-color-text-0)", fontSize: 14 }}>
        {label}
        <span style={{ fontSize: 12, color: "var(--semi-color-text-2)", marginLeft: 10 }}>
          {item.labelSubText}
        </span>
      </div>
    </div>
  );

  /**
   * 删除还款计划(批量)
   * 调用链: POST /repaymentPlan/delete
   * → RepaymentPlanController.delete → RepaymentPlanServiceImpl.delete
   * 校验: 不能删除已还款的、不能删除系统自动生成的(createdBy<=0)
   */
  const deleteRepaymentPlan = (row: any, rows: any) => {
    if (!rows || rows.length == 0) {
      Toast.info("请至少选择一个还款计划");
      return;
    }
    let ids = rows.map((item: any) => item.id);
    Modal.confirm({
      title: "是否确认删除还款计划?",
      content: "",
      onOk: async () => {
        try {
          let params = { id: ids[0], ids: ids };
          await apiClient.request("/repaymentPlan/delete", "POST", null, params).then(() => {
              Toast.success("删除成功");
              setSelectedPlanList([]);
              setCalendarRefreshKey(k => k + 1);
              // 使用 ref 刷新，避免闭包捕获旧 state
              crudApiRef.current?.refresh();
              crudApiCardRef.current?.refresh();
              crudApiBorrowRef.current?.refresh();
            });
        } catch (e) {}
      },
    });
  };

  const deleteAction: CrudAction = {
    name: "删除",
    type:"danger",
    action: deleteRepaymentPlan,
  };

  /**
   * 取消还款(批量) - 调用 cancelConfirm 接口
   * 状态机处理：
   * - PENDING / REJECTED → 直接取消还款，status=HKZ
   * - YJQ / EDIT → status=CANCEL，等待出款人确认
   * - CANCEL → 不允许操作
   */
  const handleCancelModify = (row: any, rows: any) => {
    if (!rows || rows.length == 0) {
      Toast.info("请至少选择一个还款计划");
      return;
    }
    if (rows.some((item: any) => item.status == "VR_YHZ")) {
      Toast.info("选择的记录中存在已坏账的计划，不允许操作");
      return;
    }
    if (!rows.some((item: any) => item.status == "YJQ"||item.status == "VR_YJQ"||item.status == "VR_YGZ"||item.status == "PENDING"||item.status == "REJECTED"||item.status == "EDIT")) {
      Toast.info("选择的记录中没有已还款/待审核/已驳回/编辑中的计划，无需取消还款");
      return;
    }
    const validRows = rows.filter((item: any) =>
      item.status === 'YJQ' ||item.status == "VR_YJQ"||item.status == "VR_YGZ" || item.status === 'EDIT' || item.status === 'PENDING' || item.status === 'REJECTED' 
    );
    if (validRows.length === 0) {
      Toast.info("没有可取消的记录(取消中状态不允许重复操作)");
      return;
    }
    let ids = validRows.map((item: any) => item.id);
    const needApproval = validRows.some((item: any) => item.status === 'YJQ' || item.status === 'EDIT');
    Modal.confirm({
      title: "是否确认取消还款?",
      content: needApproval ? "部分数据为已确认/编辑中状态，取消后需出款人确认才真正取消" : "",
      onOk: async () => {
        try {
          let params = { ids: ids };
          const resp = await apiClient.request("/repaymentPlan/cancelConfirm", "POST", null, params);
          const result:any = resp.data || resp;
          if (result && result.fail > 0) {
            Toast.warning(`操作完成：成功${result.success}条，失败${result.fail}条`);
          } else {
            Toast.success(`取消操作已提交，共${result?.success || validRows.length}条`);
          }
          setSelectedPlanList([]);
          setCalendarRefreshKey(k => k + 1);
          crudApiRef.current?.refresh();
          crudApiCardRef.current?.refresh();
          crudApiBorrowRef.current?.refresh();
        } catch (e) {}
      },
    });
  };

  const cancelModifyAction: CrudAction = {
    name: "取消还款",
    action: handleCancelModify,
  };

  /**
   * 撤销提交(批量) - 取消中/编辑中 → 已确认
   * 按揭专员可以撤销已发起的取消或编辑操作，还原为已确认状态
   */
  const handleWithdrawConfirm = (row: any, rows: any) => {
    if (!rows || rows.length == 0) {
      Toast.info("请至少选择一个还款计划");
      return;
    }
    const validRows = rows.filter((item: any) => item.status === 'CANCEL' || item.status === 'EDIT');
    if (validRows.length === 0) {
      Toast.info("没有可撤销的记录(仅取消中/编辑中状态可撤销)");
      return;
    }
    let ids = validRows.map((item: any) => item.id);
    Modal.confirm({
      title: "是否确认撤销提交?",
      content: `将撤销 ${validRows.length} 条记录的取消/编辑操作，还原为已确认状态`,
      onOk: async () => {
        try {
          await apiClient.request("/repaymentPlan/withdrawConfirm", "POST", null, { ids }).then(() => {
            Toast.success("撤销成功");
            setSelectedPlanList([]);
            setCalendarRefreshKey(k => k + 1);
            crudApiRef.current?.refresh();
            crudApiCardRef.current?.refresh();
            crudApiBorrowRef.current?.refresh();
          });
        } catch (e) {}
      },
    });
  };

  /**
   * 还款弹窗的确认提交处理
   * 校验: 选中计划不为空、还款金额>0、出款人/金额/日期不为空
   * 特殊: 信用卡结清时弹窗询问是否转为信贷客户
   * 调用 doRepayment → POST /repaymentPlan/doRepayment
   */
  const handleModifyClick = async () => {
    try {
      if (!selectedPlanList || selectedPlanList.length == 0) {
        Toast.info("请选择还款计划");
        return;
      }
      if (selectedPlanList.some((item: any) => item.paidAmount <= 0)) {
        Toast.info("还款金额不能小于等于0");
        return;
      }
      // 校验每条计划的出款人、金额、日期
      let validFlag = true;
      selectedPlanList.forEach((item: any) => {
        if (item.payType == "代还") {
          if (!item.paidUserId) {
            validFlag = false;
            Toast.info("代还出款人不能为空");
            return;
          }
        } else {
          item.paidUserName = item.leadName;
          if (!item.paidUserName) {
            validFlag = false;
            Toast.info("自还还款人不能为空");
            return;
          }
        }
        if (!item.paidAmount) { validFlag = false; Toast.info("还款金额不能为空"); return; }
        if (!item.paidDate) { validFlag = false; Toast.info("还款日期不能为空"); return; }
      });

      if (!validFlag) return;

      // 编辑已还款计划模式
      if (selectedPlan.paidOptType === "EDIT") {
        doEditPaidPlan();
        return;
      }

      // 信用卡结清: 询问是否将信用卡客户转为信贷客户
      if (selectedPlan.paidOptType == "JQ" && currentTab == "2") {
        Modal.confirm({
          title: "是否确认结清贷款?",
          content: "结清贷款，是否将信用卡客户转为[信贷客户]?",
          okText: "是",
          cancelText: "否",
          onOk: async () => { doRepayment(true); },
          onCancel: async () => { doRepayment(false); },
        });
      } else {
        doRepayment(false);
      }
    } catch (e) {
      setModifySaving(false);
    }
  };

  /**
   * 执行编辑已还款计划提交
   * 调用链: POST /repaymentPlan/editPaidRepaymentPlan
   * → RepaymentPlanController.editPaidRepaymentPlan → RepaymentPlanServiceImpl.editPaidRepaymentPlan
   */
  /**
   * 执行编辑已还款计划提交
   * 统一调用 editConfirm 接口，由后端状态机处理：
   * - PENDING / REJECTED → 直接编辑生效，保持 PENDING
   * - YJQ / EDIT → EDIT，存储待生效数据
   */
  const doEditPaidPlan = () => {
    try {
      setModifySaving(true);
      apiClient
        .request("/repaymentPlan/editConfirm", "POST", null, selectedPlanList)
        .then((res) => {
          const hasNeedApproval = selectedPlanList.some((item: any) =>
            item.status === 'YJQ' || item.status === 'EDIT'
          );
          Toast.success(hasNeedApproval ? "编辑已提交，待出款人确认" : "编辑成功");
          setSelectedPlan({});
          setSelectedPlanList([]);
          crudApiRef.current?.refresh();
          crudApiCardRef.current?.refresh();
          crudApiBorrowRef.current?.refresh();
          setCalendarRefreshKey(k => k + 1);
          setModifySaving(false);
          setShowModifyModal(false);
        });
    } catch (e) {
      setModifySaving(false);
    }
  };

  /**
   * 执行还款提交
   * 调用链: POST /repaymentPlan/doRepayment
   * → RepaymentPlanController.doRepayment → RepaymentPlanServiceImpl.doRepayment
   *   → 遍历 selectedPlanList，逐条更新还款计划
   *   → 根据 paidOptType(HK/JQ/GZ) + loanType + payType 确定最终 paidMethod
   *   → 更新 status, paidAmount, paidDate, paidUserId, paidMethod, paidDays, imageUrls
   *   → 如果 isTransform=true 且 信用卡结清: 更新 mortgage_loan.loan_type CARD→MORTGAGE
   */
  const doRepayment = (isTransform: boolean) => {
    try {
      setModifySaving(true);
      // 为每条计划设置是否转换标志
      selectedPlanList.forEach((item: any) => {
        item.isTransform = isTransform;
      });
      apiClient
        .request("/repaymentPlan/doRepayment", "POST", null, selectedPlanList)
        .then((res) => {
          Toast.success("提交成功");
          setSelectedPlan({});
          setSelectedPlanList([]);
          // 刷新所有三个Tab的数据(因为还款可能影响其他Tab)
          crudApiRef.current?.refresh();
          crudApiCardRef.current?.refresh();
          crudApiBorrowRef.current?.refresh();
          setCalendarRefreshKey(k => k + 1);
          setModifySaving(false);
          setShowModifyModal(false);
        }).catch(e => {
                setModifySaving(false);
            });
    } catch (e) {
      setModifySaving(false);
    }
  };

  /**
   * 发送还款提醒(批量)
   * 调用链: POST /repaymentPlan/batchCreatePayLoanTodo
   * → RepaymentPlanController.batchCreatePayLoanTodo
   * → RepaymentPlanServiceImpl.batchCreatePayLoanTodo
   * 为选中的还款计划创建待办提醒
   */
  const handleTodoClick = async (plan: any, rows: any) => {
    if (!rows || rows.length == 0) {
      Toast.info("请至少选择一个还款计划");
      return;
    }
    let ids = rows.map((item: any) => item.id);
    Modal.confirm({
      title: "是否确认发送提醒?",
      content: "",
      onOk: async () => {
        try {
          setSavingTodo(true);
          await apiClient.request("/repaymentPlan/batchCreatePayLoanTodo", "POST", undefined, { planIds: ids })
            .then(() => { Toast.info("已发送提醒信息"); });
        } catch (e) {}
      },
    });
  };
  const sendTodoAction: CrudAction = {
    name: "提醒",
    action: handleTodoClick,
  };

  /**
   * "还款"按钮处理(paidOptType="HK")
   * 校验: 选中不为空、无已还款的
   * 预填: 出款人(默认按揭专员)、金额(月供金额)、日期(应还日期)
   * 打开全屏弹窗
   */
  const handlePaymentButtonClick = (row: any, rows: any) => {
    if (!rows || rows.length == 0) { Toast.info("请至少选择一个还款计划"); return; }
    if (rows.some((item: any) => item.status == "VR_YHZ")) { Toast.info("选中的记录存在已坏账的计划，不能进行还款"); return; }
    if (rows.some((item: any) => item.status == "YJQ"||item.status == "VR_YJQ"||item.status == "VR_YGZ"||item.status == "PENDING"||item.status == "REJECTED"||item.status == "EDIT"||item.status == "CANCEL")) { Toast.info("选中的记录存在已还款/待审核/已驳回/编辑中/取消中的计划，不能进行还款"); return; }
    setModalTitle("批量还款");
    let mewRows = JSON.parse(JSON.stringify(rows));
    // 预填每条计划的还款信息
    mewRows.forEach((item: any) => {
      if (item.loanType == "BORROW") {
        // 借款: 出款人=客户(paidUserId=-99), 金额=月供, 日期=应还日期
        item.paidUserId = -99;
        item.paidUserName = item.leadName;
        item.paidAmount = item.oneTermAmount;
        item.paidDate = item.dueDate;
        item.paidOptType = "HK";
      } else {
        // 信贷/信用卡: 出款人=按揭专员(代还) 或 客户(自还)
        if (!item.paidUserId) {
          if (item.paidMethod == "KHZH") {
            item.paidUserId = -99;
            item.paidUserName = item.leadName;
          } else {
            item.paidUserId = item.agentId;
            item.paidUserName = item.agentName;
          }
          item.paidAmount = item.oneTermAmount;
          item.paidDate = item.dueDate;
        }
        item.paidOptType = "HK";
      }
      item.payType = item.paidMethod == "KHZH" ? "客户自还" : "代还";
    });
    setSelectedPlanList(mewRows);
    // 设置第一条计划为当前选中(右侧表单数据源)
    if (mewRows[0].loanType == "BORROW") {
      setSelectedPlan({ ...mewRows[0], paidOptType: "HK" });
      setLoanType("BORROW");
    } else {
      setSelectedPlan({ ...mewRows[0], paidOptType: "HK" });
      setLoanType("MORTGAGE");
    }
    setPayType(mewRows[0].paidMethod == "KHZH" ? "客户自还" : "代还");
    setShowModifyModal(true);
    setSelectedLoanAllPlanList([]);
    handleRowClick(mewRows[0]);
  };

  /**
   * "结清"按钮处理(paidOptType="JQ")
   * 逻辑同还款，区别: paidOptType="JQ"
   * 特殊: 信用卡结清时在 handleModifyClick 中询问是否转为信贷客户
   */
  const handlePaymentFinishButtonClick = (row: any, rows: any) => {
    if (!rows || rows.length == 0) { Toast.info("请至少选择一个还款计划"); return; }
    if (rows.some((item: any) => item.status == "YJQ"||item.status == "VR_YJQ"||item.status == "VR_YGZ"||item.status == "PENDING"||item.status == "REJECTED"||item.status == "EDIT"||item.status == "CANCEL")) { Toast.info("选中的记录存在已还款/待审核/已驳回/编辑中/取消中的计划，不能进行结清"); return; }
    setModalTitle("批量结清");
    let mewRows = JSON.parse(JSON.stringify(rows));
    mewRows.forEach((item: any) => {
      if (item.loanType == "BORROW") {
        item.paidUserId = -99;
        item.paidUserName = item.leadName;
        item.paidAmount = item.oneTermAmount;
        item.paidDate = item.dueDate;
        item.paidOptType = "JQ";
      } else {
        if (!item.paidUserId) {
          if (item.paidMethod == "KHZH") {
            item.paidUserId = -99;
            item.paidUserName = item.leadName;
          } else {
            item.paidUserId = item.agentId;
            item.paidUserName = item.agentName;
          }
          item.paidAmount = item.oneTermAmount;
          item.paidDate = item.dueDate;
        }
        item.paidOptType = "JQ";
      }
      item.payType = item.paidMethod == "KHZH" ? "客户自还" : "代还";
    });
    setSelectedPlanList(mewRows);
    if (mewRows[0].loanType == "BORROW") {
      setSelectedPlan({ ...mewRows[0], paidOptType: "JQ" });
      setLoanType("BORROW");
    } else {
      setSelectedPlan({ ...mewRows[0], paidOptType: "JQ" });
      setLoanType("MORTGAGE");
    }
    setPayType(mewRows[0].paidMethod == "KHZH" ? "客户自还" : "代还");
    setShowModifyModal(true);
    setSelectedLoanAllPlanList([]);
    handleRowClick(mewRows[0]);
  };

  /**
   * "过账"按钮处理(paidOptType="GZ")
   * 仅信用卡Tab(currentTab="2")可用
   * 逻辑同还款，区别: paidOptType="GZ"
   */
  const handlePaymentGzButtonClick = (row: any, rows: any) => {
    if (currentTab != "2") { Toast.info("只有信用卡客户才能进行过账"); return; }
    if (!rows || rows.length == 0) { Toast.info("请至少选择一个还款计划"); return; }
    if (rows.some((item: any) => item.status == "YJQ"||item.status == "VR_YJQ"||item.status == "VR_YGZ"||item.status == "PENDING"||item.status == "REJECTED"||item.status == "EDIT"||item.status == "CANCEL")) { Toast.info("选中的记录存在已还款/待审核/已驳回/编辑中/取消中的计划，不能进行过账"); return; }
    setModalTitle("批量过账");
    let mewRows = JSON.parse(JSON.stringify(rows));
    mewRows.forEach((item: any) => {
      if (item.loanType == "BORROW") {
        item.paidUserId = -99;
        item.paidUserName = item.leadName;
        item.paidAmount = item.oneTermAmount;
        item.paidDate = item.dueDate;
        item.paidOptType = "GZ";
      } else {
        if (!item.paidUserId) {
          if (item.paidMethod == "KHZH") {
            item.paidUserId = -99;
            item.paidUserName = item.leadName;
          } else {
            item.paidUserId = item.agentId;
            item.paidUserName = item.agentName;
          }
          item.paidAmount = item.oneTermAmount;
          item.paidDate = item.dueDate;
        }
        item.paidOptType = "GZ";
      }
      item.payType = item.paidMethod == "KHZH" ? "客户自还" : "代还";
    });
    setSelectedPlanList(mewRows);
    if (mewRows[0].loanType == "BORROW") {
      setSelectedPlan({ ...mewRows[0], paidOptType: "GZ" });
      setLoanType("BORROW");
    } else {
      setSelectedPlan({ ...mewRows[0], paidOptType: "GZ" });
      setLoanType("MORTGAGE");
    }
    setPayType(mewRows[0].paidMethod == "KHZH" ? "客户自还" : "代还");
    setShowModifyModal(true);
    setSelectedLoanAllPlanList([]);
    handleRowClick(mewRows[0]);
  };

  /**
   * "编辑"按钮处理 - 编辑已还款计划的出款信息
   * 仅允许选中已还款(YJQ/VR_YJQ/VR_YGZ)状态的计划
   * 预填当前出款信息，设置 paidOptType="EDIT"
   */
  const handleEditButtonClick = (row: any, rows: any) => {
    if (!rows || rows.length == 0) { Toast.info("请至少选择一个还款计划"); return; }
    if (rows.some((item: any) => item.status !== "YJQ" && item.status !== "VR_YJQ" && item.status !== "VR_YGZ" && item.status !== "PENDING" && item.status !== "REJECTED" && item.status !== "EDIT")) {
      Toast.info("只能编辑已还款/待审核/已驳回/编辑中的计划");
      return;
    }
    setModalTitle("编辑出款信息");
    let mewRows = JSON.parse(JSON.stringify(rows));
    mewRows.forEach((item: any) => {
      item.paidOptType = "EDIT";
      // 根据现有paidUserId判断还款方式
      item.payType = item.paidUserId == -99 ? "客户自还" : "代还";
    });
    setSelectedPlanList(mewRows);
    const first = mewRows[0];
    setSelectedPlan({ ...first, paidOptType: "EDIT" });
    setLoanType(first.loanType == "BORROW" ? "BORROW" : "MORTGAGE");
    setPayType(first.paidUserId == -99 ? "客户自还" : "代还");
    setShowModifyModal(true);
    setSelectedLoanAllPlanList([]);
    handleRowClick(mewRows[0]);
  };

  // 批量操作按钮定义
  const paymentAction: CrudAction = { name: "还款", action: handlePaymentButtonClick };
  const paymentFinishAction: CrudAction = { name: "结清", action: handlePaymentFinishButtonClick };
  const paymentGzAction: CrudAction = { name: "过账", action: handlePaymentGzButtonClick };

  let nowDate = new Date();
  let nowDateStr = formatDate(nowDate);
  let startDate = new Date(nowDate.getFullYear(), nowDate.getMonth() - 1, 1);
  let endDate = new Date(nowDate.getFullYear(), nowDate.getMonth() + 1, 0);
  const [initFilterValues, setInitFilterValues] = useState({});
  let startDateStr = formatDate(startDate);
  let endDateStr = formatDate(endDate);
  const initParams = {};

  // 根据当前Tab动态过滤"过账"按钮(仅信用卡Tab显示)
  const updateBatchActions = (tab: string) => {
    if (tab != "2") {
      setBatchActions(actions.filter((item, index) => index != 2));
    } else {
      setBatchActions(actions);
    }
  };

  // Tab切换处理
  const onTabSelect = (e: any) => {
    setCurrentTab(e);
    updateBatchActions(e);
  };

  // 初始化 & currentTab变化时同步按钮
  useEffect(() => {
    updateBatchActions(currentTab);
  }, [currentTab]);

  /**
   * 打开客户详情页(新窗口)
   * 跳转到 /#/newlead 页面，传入客户ID和查询参数
   */
  const detailAction = (a: any) => {
    const w = window.open("about:blank");
    let queryParams = { ...crudApi?.getQueryParams(), type: "all" };
    if (w != null) {
      const ids = crudApi?.getData()?.map((i) => i.leadId);
      const data = {
        id: a.leadId,
        ids: ids,
        viewMode: true,
        visitMode: false,
        leadFrom: "report",
        queryParams: queryParams,
      };
      w.location.href = "/#/leadDetailNew?data=" + encodeURIComponent(JSON.stringify(data));
    }
  };

  // 功能12: 打开贷款详情页面
  // 跳转到新窗口 /#/newleadLoan，传入客户ID和姓名
  // 对应二级页面 indexMortgageLoan.tsx (entityKey: mortgageLoan/listGroupPlatform)
  const openMortgageLoanModal = async (leadId: number, leadName: string) => {
    //setLeadId(leadId);
    //setMortgageLoanShow(true);
    //打开客户贷款
    const w = window.open("about:blank");
    if (w != null) {
      //获取表格data的id列表
      const data = {
        id: leadId,
        name: leadName,
      };
      w.location.href =
        "/#/newleadLoan?data=" + encodeURIComponent(JSON.stringify(data));
    }
  };


  // ====== dueDate 快捷筛选 ======
  const crudApiRef = useRef<CrudApi>();
  const crudApiCardRef = useRef<CrudApi>();
  const crudApiBorrowRef = useRef<CrudApi>();
  const filterFormRef = useRef<any>();
  const filterFormCardRef = useRef<any>();
  const filterFormBorrowRef = useRef<any>();

  // 所有批量操作按钮(按顺序): 还款、结清、过账、提醒、取消还款、删除
  const actions: CrudAction[] = [
    { name: "还款", type:"warning", action: handlePaymentButtonClick },
    { name: "结清", type:"warning", action: handlePaymentFinishButtonClick },
    { name: "过账", type:"warning", action: handlePaymentGzButtonClick },
    { name: "编辑", type:"warning", action: handleEditButtonClick },
    { name: "取消还款", type:"danger", action: handleCancelModify },
    { name: "撤销提交", type:"tertiary", action: handleWithdrawConfirm },
    { name: "删除", type:"danger", action: deleteRepaymentPlan },
    { name: "提醒", type:"primary", action: handleTodoClick },

  ];

  // ====== Tab1 信贷 统计数据 ======
  useEffect(() => {
    initStatCountData();
  }, [statDataObj]);

  /**
   * Tab1 统计汇总查询
   * 调用链: GET /repaymentPlan/listSum?loanType=MORTGAGE&...筛选参数
   * → RepaymentPlanController.listSum → RepaymentPlanServiceImpl.queryRepaymentPlanListSum
   * → RepaymentPlanMapper.selectRepaymentPlanListSum
   */
  const queryStatCount = async (data: any) => {
    let params = { ...data };
    apiClient.request("/repaymentPlan/listSum", "GET", data).then((resp) => {
      setStatDataObj(resp.data);
    });
  };

    const queryConfirmStatusCount = async (data: any) => {
    apiClient.request("/repaymentPlan/confirmStatusCountHkjh", "GET", data).then((resp) => {
      setConfirmStatusCounts(resp.data);
    });
  };

  // 格式化Tab1信贷统计数据为 Descriptions 组件所需的数组格式
  const initStatCountData = async () => {
    if (!statDataObj) return;
    const data = [
      { key: "", value: "汇总" },
      { key: "贷款金额", value: formatAmountZhCn(statDataObj.amount) },
      { key: "代还金额", value: formatAmountZhCn(statDataObj.loanHelpRepaymentPlanAmount) },
      { key: "客户自还金额", value: formatAmountZhCn(statDataObj.loanRepaymentPlanAmount - statDataObj.loanHelpRepaymentPlanAmount) },
      { key: "月供汇总", value: statDataObj.oneTermAmount },
      { key: "", value: "今日/本月" },
      { key: "今日待还金额", value: formatAmountZhCn(statDataObj.dayMortgageOneTermAmount) },
      { key: "今日已还金额", value: formatAmountZhCn(statDataObj.dayMortgagePaidAmount) },
      { key: "本月待还金额", value: formatAmountZhCn(statDataObj.monthMortgageOneTermAmount) },
      { key: "本月已还金额", value: formatAmountZhCn(statDataObj.monthMortgagePaidAmount) },
    ];
    setStatData(data);
  };

  const safeSubNotLessZero = (a: any, b: any) => {
    let c = a - b;
    if (c < 0) c = 0;
    return c;
  };

  // Tab1 信贷统计栏渲染
  const listSumStat = () => {
    const style = {
      boxShadow: "var(--semi-shadow-elevated)",
      backgroundColor: "var(--semi-color-bg-2)",
      borderRadius: "4px",
      padding: "10px",
      margin: "auto",
    };
    return (
      <div style={{ display: "flex", width: "100%", margin: "5px", alignItems: "center" }}>
        <Popover content={renderStatDetailContentPlan(statDataObj)} trigger="hover" position="bottomLeft" showArrow>
          <a style={{ marginRight: 12, whiteSpace: 'nowrap', color: 'var(--semi-color-link)', cursor: 'pointer' }}><IconBarChartHStroked style={{marginRight: 4}}/>统计详情</a>
        </Popover>
        <Descriptions data={statData} row style={style} />
      </div>
    );
  };

  // ====== Tab2 信用卡 统计数据 ======
  useEffect(() => {
    initStatCountDataCard();
  }, [statDataObjCard]);

  /**
   * Tab2 信用卡统计汇总查询
   * 调用链: GET /repaymentPlan/listSum?loanType=CARD&...筛选参数
   */
  const queryStatCountCard = async (data: any) => {
    apiClient.request("/repaymentPlan/listSum", "GET", data).then((resp) => {
      setStatDataObjCard(resp.data);
    });
  };

  const queryConfirmStatusCountCard = async (data: any) => {
    apiClient.request("/repaymentPlan/confirmStatusCountHkjh", "GET", data).then((resp) => {
      setConfirmStatusCountsCard(resp.data);
    });
  };

  

  // 格式化Tab2信用卡统计数据(含过账金额)
  const initStatCountDataCard = async () => {
    if (!statDataObjCard) return;
    const dataCard = [
      { key: "", value: "汇总" },
      { key: "贷款金额", value: formatAmountZhCn(statDataObjCard.amount) },
      { key: "代还金额", value: formatAmountZhCn(statDataObjCard.cardHelpRepaymentPlanAmount) },
      { key: "代过账金额", value: formatAmountZhCn(statDataObjCard.cardHelpPostingPlanAmount) },
      { key: "客户自还金额", value: formatAmountZhCn(statDataObjCard.cardRepaymentPlanAmount - statDataObjCard.cardHelpRepaymentPlanAmount + statDataObjCard.cardPostingPlanAmount - statDataObjCard.cardHelpPostingPlanAmount) },
      { key: "月供汇总", value: statDataObjCard.oneTermAmount },
      { key: "", value: "今日/本月" },
      { key: "今日待还金额", value: formatAmountZhCn(statDataObjCard.dayMortgageOneTermAmount) },
      { key: "今日已还金额", value: formatAmountZhCn(statDataObjCard.dayMortgagePaidAmount) },
      { key: "本月待还金额", value: formatAmountZhCn(statDataObjCard.monthMortgageOneTermAmount) },
      { key: "本月已还金额", value: formatAmountZhCn(statDataObjCard.monthMortgagePaidAmount) },
    ];
    setStatDataCard(dataCard);
  };

  // Tab2 信用卡统计栏渲染
  const listSumStatCard = () => {
    const style = {
      boxShadow: "var(--semi-shadow-elevated)",
      backgroundColor: "var(--semi-color-bg-2)",
      borderRadius: "4px",
      padding: "10px",
      margin: "auto",
    };
    return (
      <div style={{ display: "flex", width: "100%", margin: "5px", alignItems: "center" }}>
        <Popover content={renderStatDetailContentPlan(statDataObjCard)} trigger="hover" position="bottomLeft" showArrow>
          <a style={{ marginRight: 12, whiteSpace: 'nowrap', color: 'var(--semi-color-link)', cursor: 'pointer' }}><IconBarChartHStroked style={{marginRight: 4}}/>统计详情</a>
        </Popover>
        <Descriptions data={statDataCard} row style={style} />
      </div>
    );
  };

  // ====== Tab3 个人借款 统计数据 ======
  useEffect(() => {
    initStatCountDataBorrow();
  }, [statDataObjBorrow]);

  /**
   * Tab3 个人借款统计汇总查询
   * 调用链: GET /repaymentPlan/listSum?loanType=BORROW&...筛选参数
   */
  const queryStatCountBorrow = async (data: any) => {
    apiClient.request("/repaymentPlan/listSum", "GET", data).then((resp) => {
      setStatDataObjBorrow(resp.data);
    });
  };

  const queryConfirmStatusCountBorrow = async (data: any) => {
    apiClient.request("/repaymentPlan/confirmStatusCountHkjh", "GET", data).then((resp) => {
      setConfirmStatusCountsBorrow(resp.data);
    });
  };

  // 格式化Tab3个人借款统计数据(含出借金额、归还金额、剩余金额)
  const initStatCountDataBorrow = async () => {
    if (!statDataObjBorrow) return;
    const dataBorrow = [
      { key: "", value: "汇总" },
      { key: "出借金额", value: formatAmountZhCn(statDataObjBorrow.borrowAmount) },
      { key: "借款归还金额", value: formatAmountZhCn(statDataObjBorrow.borrowReturnAmount) },
      { key: "剩余金额", value: formatAmountZhCn(statDataObjBorrow.borrowAmount - statDataObjBorrow.borrowReturnAmount) },
      { key: "", value: "今日/本月" },
      { key: "今日待还金额", value: formatAmountZhCn(statDataObjBorrow.dayBorrowOneTermAmount) },
      { key: "今日已还金额", value: formatAmountZhCn(statDataObjBorrow.dayBorrowPaidAmount) },
      { key: "本月待还金额", value: formatAmountZhCn(statDataObjBorrow.monthBorrowOneTermAmount) },
      { key: "本月已还金额", value: formatAmountZhCn(statDataObjBorrow.monthBorrowPaidAmount) },
    ];
    setStatDataBorrow(dataBorrow);
  };

  // Tab3 个人借款统计栏渲染
  const listSumStatBorrow = () => {
    const style = {
      boxShadow: "var(--semi-shadow-elevated)",
      backgroundColor: "var(--semi-color-bg-2)",
      borderRadius: "4px",
      padding: "10px",
      margin: "auto",
    };
    return (
      <div style={{ display: "flex", width: "100%", margin: "5px", alignItems: "center" }}>
        <Popover content={renderStatDetailContentPlan(statDataObjBorrow)} trigger="hover" position="bottomLeft" showArrow>
          <a style={{ marginRight: 12, whiteSpace: 'nowrap', color: 'var(--semi-color-link)', cursor: 'pointer' }}><IconBarChartHStroked style={{marginRight: 4}}/>统计详情</a>
        </Popover>
        <Descriptions data={statDataBorrow} row style={style} />
      </div>
    );
  };

  /**
   * 弹窗内行点击事件处理
   * 点击已选计划表格中的某一行 → 更新右侧表单 + 加载该贷款的所有还款计划
   * 调用链: GET /repaymentPlan/selectRepaymentPlanList?loanId=
   * → RepaymentPlanController.selectRepaymentPlanList
   * → RepaymentPlanServiceImpl.selectRepaymentPlanListByLoanId
   */
  const handleRowClick = (record: any) => {
    if (record.leadName == "汇总") return; // 汇总行不可点击
    const { ...rest } = record;
    setPayType(record.payType);
    setSelectedPlan(rest);
    if (modifyFormApi) {
      modifyFormApi.setValues(rest);
    }
    // 查询该贷款的所有还款计划(弹窗左下表格)
    apiClient
      .request("/repaymentPlan/selectRepaymentPlanList", "GET", { loanId: record.loanId })
      .then((resp) => { setSelectedLoanAllPlanList(resp.data); });
  };

  /**
   * 点击平台链接 → 打开贷款详情弹窗
   * 上方: 贷款汇总卡片(贷款金额/已还金额/剩余未还)
   * 下方: 该贷款全部还款计划列表
   */
  const openLoanDetail = (record: any) => {
    setLoanDetailRecord(record);
    setLoanDetailPlans([]);
    setShowLoanDetailModal(true);
    apiClient
      .request("/repaymentPlan/selectRepaymentPlanList", "GET", { loanId: record.loanId })
      .then((resp: any) => { setLoanDetailPlans(resp.data || []); });
  };

  /**
   * 弹窗内表单字段提交处理(实时更新)
   * valueType: 1=paidUserId, 2=paidAmount, 3=paidDate
   * 同步更新 selectedPlan 和 selectedPlanList 中对应的行
   */
  const handleFormSubmit = async (value: any, valueType: any) => {
    const values: any = modifyFormApi?.getValues();
    if (valueType == 1) values.paidUserId = value;
    else if (valueType == 2) values.paidAmount = value;
    else if (valueType == 3) values.paidDate = value;

    // 从团队成员列表中查找出款人姓名
    let paidUserName = "";
    teamMembersTree.forEach((item: any) => {
      if (item.value === values.paidUserId) paidUserName = item.label;
    });

    const updated = {
      ...selectedPlan,
      paidUserId: values.paidUserId,
      paidUserName: paidUserName,
      paidAmount: values.paidAmount,
      paidDate: formatDate(values.paidDate),
    };
    setSelectedPlan(updated);

    // 同步更新批量列表中对应id的行
    const updatedData = selectedPlanList.map((p: any) =>
      p.id === selectedPlan.id
        ? { ...p, paidUserId: values.paidUserId, paidUserName, paidAmount: values.paidAmount, paidDate: formatDate(values.paidDate) }
        : p
    );
    setSelectedPlanList(updatedData);
  };

  // 弹窗内已选计划的汇总数据(月供总额、还款总额)
  const summary = useMemo(() => {
    if (selectedPlanList && selectedPlanList.length > 0) {
      return {
        oneTermAmount: safeSumBy(selectedPlanList, "oneTermAmount"),
        paidAmount: safeSumBy(selectedPlanList, "paidAmount"),
      };
    } else {
      return { oneTermAmount: 0 };
    }
  }, [selectedPlanList]);

  // 安全求和工具函数(过滤NaN和非法值)
  function safeSumBy<T>(items: T[], prop: keyof T): number {
    return items.reduce((sum, item) => {
      const value = item[prop];
      if (typeof value === "number" && !isNaN(value)) return sum + value;
      const numericValue = Number(value);
      return !isNaN(numericValue) ? sum + numericValue : sum;
    }, 0);
  }

  // 已选计划表格数据 = 汇总行(id=999999) + 选中计划列表
  const dataWithSummary = [
    { key: "summary", leadName: "汇总", id: 999999, leadId: 999999, oneTermAmount: summary.oneTermAmount, paidAmount: summary.paidAmount },
    ...selectedPlanList,
  ];

  // 该贷款所有还款计划的汇总数据
  const summaryAll = useMemo(() => {
    if (selectedLoanAllPlanList && selectedLoanAllPlanList.length > 0) {
      return {
        amount: selectedLoanAllPlanList[0]["amount"],
        oneTermAmount: safeSumBy(selectedLoanAllPlanList, "oneTermAmount"),
        paidAmount: safeSumBy(selectedLoanAllPlanList, "paidAmount"),
        postingAmount: safeSumBy(selectedLoanAllPlanList, "postingAmount"),
      };
    } else {
      return { oneTermAmount: 0 };
    }
  }, [selectedLoanAllPlanList]);

  // 所有计划表格数据 = 汇总行(id=999998) + 所有还款计划
  const dataAllWithSummary = [
    { key: "summary", periodNo: "汇总", id: 999998, amount: summaryAll.amount, oneTermAmount: summaryAll.oneTermAmount, paidAmount: summaryAll.paidAmount, postingAmount: summaryAll.postingAmount },
    ...selectedLoanAllPlanList,
  ];

  /**
   * 切换还款方式(代还/客户自还)
   * 代还: 出款人改为按揭专员，显示出款人下拉选择
   * 自还: 出款人改为客户姓名，出款人输入框禁用
   */
  const changePayType = (payType: any) => {
    setPayType(payType);
    if (payType === "代还") {
      modifyFormApi!.setValue("paidUserName", selectedPlan.agentName);
      modifyFormApi!.setValue("paidUserId", selectedPlan.agentId);
      let selectedPlanListUpd = selectedPlanList.map((item: any) => {
        if (item.id === selectedPlan.id) {
          item.paidUserName = selectedPlan.agentName;
          item.payType = "代还";
        }
        return item;
      });
      setSelectedPlanList(selectedPlanListUpd);
      setSelectedPlan({ ...selectedPlan, paidUserName: selectedPlan.agentName, payType: "代还" });
    } else {
      modifyFormApi!.setValue("paidUserName", selectedPlan.leadName);
      let selectedPlanListUpd = selectedPlanList.map((item: any) => {
        if (item.id === selectedPlan.id) {
          item.paidUserName = selectedPlan.leadName;
          item.payType = "客户自还";
        }
        return item;
      });
      setSelectedPlanList(selectedPlanListUpd);
      setSelectedPlan({ ...selectedPlan, paidUserName: selectedPlan.leadName, payType: "客户自还" });
    }
  };

  /**
   * 图片上传成功回调
   * 调用链: POST /attachment/upload → AttachmentController.upload
   * 将返回的图片URL添加到 selectedPlan.imageUrls 数组
   */
  const handleUploadSuccess = (response: any, file: any) => {
    if(response.code === 'ok'){
      if (!selectedPlan) return;
      if(!selectedPlan.imageUrls) selectedPlan.imageUrls = [];
      const path = { uid: Date.now(), url: response.data };
      const updated = { ...selectedPlan, imageUrls: [...selectedPlan.imageUrls, path] };
      setSelectedPlan(updated);
      modifyFormApi!.setValue("imageUrls", updated.imageUrls);
      setSelectedPlanList(prev => prev.map(item => item.id === updated.id ? updated : item));
    }
  };

  /**
   * 图片删除回调
   * 从 selectedPlan.imageUrls 中移除指定图片
   */
  const handleRemoveImage = (fileItem: FileItem) => {
    if(!fileItem.url) return;
    if (!selectedPlan) return;
    if(selectedPlan.imageUrls && selectedPlan.imageUrls.length > 0) {
      const fileItemUrl = fileItem.url.startsWith('blob:') ? fileItem.url.substring(5) : fileItem.url;
      let index = -1;
      for(let i=0; i < selectedPlan.imageUrls.length; i++){
        if(selectedPlan.imageUrls[i].url === fileItemUrl){
          index = i;
          break;
        }
      }
      if (index !== -1) {
        const newUrls = [...selectedPlan.imageUrls];
        newUrls.splice(index, 1);
        const updated = { ...selectedPlan, imageUrls: newUrls };
        setSelectedPlan(updated);
        setSelectedPlanList(prev => prev.map(item => item.id === updated.id ? updated : item));
      }
    }
  };

  return (
    <>
      {/* ====== 还款弹窗(全屏) ======
          结构:
            左上(span=18): 已选计划表格(带汇总行，点击行切换右侧表单)
            右侧(span=6): 还款信息表单(还款方式、出款人、金额、日期、图片)
            左下(span=18): 该贷款所有还款计划表格(带汇总行)
      */}
      <Modal
        icon={<IconModalStroked size={"extra-large"} style={{ color: "blue" }} />}
        fullScreen={true}
        closeOnEsc={false}
        title={modalTitle}
        visible={showModifyModal}
        okText={modalTitle}
        confirmLoading={modifySaving}
        onOk={handleModifyClick}
        onCancel={() => setShowModifyModal(false)}
        style={{overflow:'auto'}}
      >
        <Form getFormApi={(formApi) => setModifyFormApi(formApi)} initValues={selectedPlan}>
          <Row style={{height:440}}>
            <RowCol span={18}>
              <Card
                title="已选计划"
                style={{ marginBottom: 8 }}
                headerStyle={{ padding: '8px 16px', fontWeight: 600, fontSize: 14 }}
                bodyStyle={{ padding: 8 }}
              >
                <Table
                  size="small"
                  scroll={{y:300}}
                  rowKey="id"
                  onRow={(record: any, index) => {
                    return {
                      style: {
                        cursor: record.key === "summary" ? "default" : "pointer",
                        backgroundColor: selectedPlan.id === record.id ? "var(--semi-color-primary-light-hover)" : (record.key === "summary" ? "var(--semi-color-fill-0)" : "transparent"),
                        fontWeight: record.key === "summary" ? 600 : 400,
                        transition: "background-color 0.2s",
                      },
                      onClick: () => { handleRowClick(record); },
                    };
                  }}
                  columns={[
                    {
                      title: "期数",
                      dataIndex: "periodNo",
                      width: 120,
                      render: (value, record, index) => {
                        if (record.createdBy && record.createdBy > 0) {
                          return (<Tag color={"blue"} key={value} size="small" shape="circle" type="solid"> {value}期/{record.maxPeriodNo}期 </Tag>);
                        } else if (record.paidUserId == -99) {
                          return (<Tag color={"orange"} key={value} size="small" shape="circle" type="solid"> {value}期/{record.maxPeriodNo}期 </Tag>);
                        } else {
                          return value ? value + "期/" + record.maxPeriodNo + "期" : '';
                        }
                      },
                    },
                    {
                      title: "客户姓名",
                      dataIndex: "leadName",
                      width: 100,
                      render: (text: any, record: any) =>
                        record.key === "summary" ? (<Text strong>{text}</Text>) : (text),
                    },
                    { title: "平台", dataIndex: "platform", width: 100 },
                    { title: "还款方式", dataIndex: "payType", width: 90 },
                    {
                      title: "应还金额",
                      dataIndex: "oneTermAmount",
                      width: 110,
                      render: (text: number, record: any) =>
                        record.key === "summary" ? (<Text strong style={{ color: 'var(--semi-color-primary)' }}>{formatAmountZhCn(text)}</Text>) : (<Text strong>{formatAmountZhCn(text)}</Text>),
                    },
                    { title: "还款日期", dataIndex: "dueDate", width: 100 },
                    { title: "出款人", dataIndex: "paidUserName", width: 80 },
                    {
                      title: "出款金额",
                      dataIndex: "paidAmount",
                      width: 110,
                      render: (text: number, record: any) =>
                        record.key === "summary" ? (<Text strong style={{ color: '#e91e63' }}>{formatAmountZhCn(text)}</Text>) : (<Text strong style={{ color: 'var(--semi-color-success)' }}>{formatAmountZhCn(text)}</Text>),
                    },
                    { title: "出款日期", dataIndex: "paidDate", width: 100 },
                    {
                      title: "还款图片",
                      dataIndex: "imageUrls",
                      width: 60,
                      render: (value, record, index) => {
                        if (!record.imageUrls || record.imageUrls.length == 0) return <Text type="tertiary">-</Text>;
                        return (<Image width={40} height={40}
                          src={record.imageUrls[0].url}
                          preview={{ src: (record.imageUrls || []).map(item => item.url) }}
                        />);
                      }
                    },
                  ]}
                  pagination={false}
                  dataSource={dataWithSummary}
                  bordered
                />
              </Card>
            </RowCol>

            {/* 右侧: 还款信息表单 */}
            <RowCol span={6}>
              <Card
                title="还款信息"
                style={{ marginLeft: 12 }}
                headerStyle={{ padding: '8px 16px', fontWeight: 600, fontSize: 14 }}
                bodyStyle={{ padding: '12px 16px' }}
              >
                {/* 还款方式选择: 信贷=代还/自还, 借款=仅自还 */}
                {loanType === "MORTGAGE" && (
                  <Form.RadioGroup
                    field="payType"
                    initValue={payType}
                    label="还款方式"
                    onChange={(e) => changePayType(e.target.value)}
                    style={{ marginBottom: 4 }}
                  >
                    <Form.Radio value="代还">代还</Form.Radio>
                    <Form.Radio value="客户自还">客户自还</Form.Radio>
                  </Form.RadioGroup>
                )}

                {loanType === "BORROW" && (
                  <Form.RadioGroup
                    field="payType"
                    initValue={payType}
                    label="还款方式"
                    onChange={(e) => changePayType(e.target.value)}
                    style={{ marginBottom: 4 }}
                  >
                    <Form.Radio value="客户自还">客户自还</Form.Radio>
                  </Form.RadioGroup>
                )}

                {/* 代还: 显示出款人下拉选择(团队成员) */}
                {payType === "代还" && (
                  <Form.Select
                    placeholder="请选择出款人"
                    rules={[{ required: true, message: "请选择出款人" }]}
                    label={"出款人"}
                    field="paidUserId"
                    showClear={true}
                    filter={true}
                    style={{ width: '100%' }}
                    onChange={(value: any) => handleFormSubmit(value, 1)}
                  >
                    {teamMembersTree.map((l) => (
                      <Form.Select.Option key={l.value + ""} value={l.value}>
                        {l.label}-{l.city}-{l.teamName}
                      </Form.Select.Option>
                    ))}
                  </Form.Select>
                )}
                {/* 自还: 出款人=客户姓名(禁用) */}
                {payType === "客户自还" && (
                  <Form.Input style={{ width: '100%' }} key="paidUserName" label="出款人" field="paidUserName" disabled />
                )}

                {/* 还款金额、实际还款日期 */}
                <Form.InputNumber
                  rules={[{ required: true, message: "请填写" }]}
                  style={{ width: '100%' }}
                  key="paidAmount"
                  label="还款金额"
                  field="paidAmount"
                  showClear={true}
                  onChange={(value: any) => handleFormSubmit(value, 2)}
                />
                <Form.DatePicker
                  rules={[{ required: true, message: "请选择实际还款日期" }]}
                  style={{ width: '100%' }}
                  type="date"
                  label="实际还款日期"
                  field="paidDate"
                  showClear={true}
                  onChange={(value: any) => handleFormSubmit(value, 3)}
                />
                {/* 还款图片上传 */}
                <Form.Upload
                  label="还款图片"
                  field="imageUrls"
                  action={getEndpointUrl()+"/attachment/upload"}
                  onSuccess={handleUploadSuccess}
                  listType="picture"
                  name="file"
                  renderThumbnail={(file) => (<Image src={file.url} />)}
                  onRemove={(_file, _fileList, fileItem) => { handleRemoveImage(fileItem) }}
                  accept="image/*"
                >
                  <Button icon={<IconPlus />} theme="light">上传图片</Button>
                </Form.Upload>
              </Card>
            </RowCol>
          </Row>
          <Row style={{height:320}}>
            <RowCol span={18}>
              {/* 该贷款所有还款计划表格(带汇总行) */}
              <Card
                title="所有计划"
                style={{ marginLeft: 0, height: 300 }}
                headerStyle={{ padding: '8px 16px', fontWeight: 600, fontSize: 14 }}
                bodyStyle={{ padding: 8 }}
              >
                <Table
                  size="small"
                  scroll={{y:280}}
                  rowKey="id"
                  onRow={(record: any) => ({
                    style: {
                      fontWeight: record.key === "summary" ? 600 : 400,
                      backgroundColor: record.key === "summary" ? "var(--semi-color-fill-0)" : "transparent",
                    },
                  })}
                  columns={[
                    {
                      title: "期数",
                      dataIndex: "periodNo",
                      width: 80,
                      render: (value, record, index) => {
                        if(record.key === "summary") return <Text strong>{value}</Text>;
                        if (record.createdBy && record.createdBy > 0) {
                          return (<Tag color={"blue"} key={value} size="small" shape="circle" type="solid"> {value}期 </Tag>);
                        } else if (record.paidUserId == -99) {
                          return (<Tag color={"orange"} key={value} size="small" shape="circle" type="solid"> {value}期 </Tag>);
                        } else {
                          return value + "期";
                        }
                      },
                    },
                    {
                      title: "贷款金额",
                      dataIndex: "amount",
                      width: 110,
                      render: (text: number, record: any) =>
                        record.key === "summary" ? (<Text strong style={{ color: 'var(--semi-color-primary)' }}>{formatAmountZhCn(text)}</Text>) : (formatAmountZhCn(text)),
                    },
                    {
                      title: "应还金额",
                      dataIndex: "oneTermAmount",
                      width: 110,
                      render: (text: number, record: any) =>
                        record.key === "summary" ? (<Text strong>{formatAmountZhCn(text)}</Text>) : (<Text strong>{formatAmountZhCn(text)}</Text>),
                    },
                    { title: "还款日期", dataIndex: "dueDate", width: 100 },
                    { title: "出款人", dataIndex: "paidUserName", width: 80 },
                    {
                      title: "实际还款金额",
                      dataIndex: "paidAmount",
                      width: 120,
                      render: (text: number, record: any) =>
                        record.key === "summary" ? (<Text strong style={{ color: '#e91e63' }}>{formatAmountZhCn(text)}</Text>) : (<Text strong style={{ color: 'var(--semi-color-success)' }}>{formatAmountZhCn(text)}</Text>),
                    },
                    { title: "实际还款日期", dataIndex: "paidDate", width: 100 },
                    {
                      title: "过账金额",
                      dataIndex: "postingAmount",
                      width: 110,
                      render: (text: number, record: any) =>
                        record.key === "summary" ? (<Text strong style={{ color: 'var(--semi-color-tertiary)' }}>{formatAmountZhCn(text)}</Text>) : (formatAmountZhCn(text)),
                    },
                    {
                      title: "还款方式",
                      dataIndex: "paidMethod",
                      width: 120,
                      render: (text: any, record: any) => displayPaidMethod(text),
                    },
                    {
                      title: "状态",
                      dataIndex: "status",
                      width: 100,
                      render: (text: any, record: any) => {
                        const tagConfig = {
                          YJQ: { color: "green", text: "已还款" },
                          YQ: { color: "pink", text: "已延期" },
                          HKZ: { color: "cyan", text: "还款中" },
                        };
                        const tagProps = tagConfig[text];
                        if (!tagProps) return text;
                        return (<Tag shape="circle" {...tagProps} style={{ userSelect: "text" }}>{tagProps.text}</Tag>);
                      },
                    },
                  ]}
                  pagination={false}
                  dataSource={dataAllWithSummary}
                  bordered
                />
              </Card>
            </RowCol>
          </Row>
        </Form>
      </Modal>

      {/* 贷款详情弹窗 - 点击平台链接打开 */}
      <Modal
        title={loanDetailRecord ? `${loanDetailRecord.leadName} - ${loanDetailRecord.platform || ''} 贷款详情` : '贷款详情'}
        visible={showLoanDetailModal}
        footer={null}
        onCancel={() => setShowLoanDetailModal(false)}
        width={800}
      >
        {loanDetailRecord && (
          <>
            {/* 汇总卡片 */}
            <div style={{ display: "flex", gap: 16, marginBottom: 20 }}>
              <div style={{ flex: 1, background: "#f0f5ff", borderRadius: 8, padding: "16px 20px", textAlign: "center" }}>
                <div style={{ fontSize: 20, fontWeight: "bold", color: "#1677ff" }}>{formatAmountZhCn(loanDetailRecord.amount)}</div>
                <div style={{ fontSize: 13, color: "#999", marginTop: 4 }}>贷款金额</div>
              </div>
              <div style={{ flex: 1, background: "#f0fff4", borderRadius: 8, padding: "16px 20px", textAlign: "center" }}>
                <div style={{ fontSize: 20, fontWeight: "bold", color: "#00b578" }}>
                  {formatAmountZhCn(loanDetailPlans.reduce((s: number, p: any) => s + (p.paidAmount || 0), 0))}
                </div>
                <div style={{ fontSize: 13, color: "#999", marginTop: 4 }}>已还金额</div>
              </div>
              <div style={{ flex: 1, background: (loanDetailRecord.amount - loanDetailPlans.reduce((s: number, p: any) => s + (p.paidAmount || 0), 0)) > 0 ? "#fff2f0" : "#f0fff4", borderRadius: 8, padding: "16px 20px", textAlign: "center" }}>
                <div style={{ fontSize: 20, fontWeight: "bold", color: (loanDetailRecord.amount - loanDetailPlans.reduce((s: number, p: any) => s + (p.paidAmount || 0), 0)) > 0 ? "#ff4d4f" : "#00b578" }}>
                  {formatAmountZhCn(loanDetailRecord.amount - loanDetailPlans.reduce((s: number, p: any) => s + (p.paidAmount || 0), 0))}
                </div>
                <div style={{ fontSize: 13, color: "#999", marginTop: 4 }}>剩余未还</div>
              </div>
            </div>

            {/* 全部还款计划 */}
            <Table 
              style={{ marginBottom: 20 }}
              rowKey="id"
              columns={[
                { title: "期数", dataIndex: "periodNo", render: (v: any) => `${v}期/${loanDetailPlans.reduce((m: number, p: any) => Math.max(m, p.periodNo || 0), 0)}期` },
                { title: "应还日期", dataIndex: "dueDate" },
                { title: "月供金额", dataIndex: "oneTermAmount", render: (v: number) => formatAmountZhCn(v) },
                { title: "已还金额", dataIndex: "paidAmount", render: (v: number) => formatAmountZhCn(v) },
                { title: "还款日期", dataIndex: "paidDate" },
                { title: "出款人", dataIndex: "paidUserName" },
                {
                  title: "状态", dataIndex: "status",
                  render: (value: any, record: any) => {
                    let color: any = "blue";
                    if (record.paidMethod && record.paidMethod.includes("_JQ_")) return <Tag color="pink" shape="circle" type="solid">已结清</Tag>;
                    if (value === "YJQ") color = "green";
                    else if (value === "YQ") color = "red";
                    return <Tag color={color} shape="circle" type="solid">{displayRepaymentPlanStatus(value)}</Tag>;
                  },
                },
              ]}
              dataSource={loanDetailPlans}
              pagination={false}
              bordered
              size="small"
            />
          </>
        )}
      </Modal>

      {/* ====== 确认日志弹窗 ====== */}
      <Modal
        title={confirmLogRecord ? `确认状态 - ${confirmLogRecord.leadName}(${confirmLogRecord.leadId}) 第${confirmLogRecord.periodNo}期` : '确认状态'}
        visible={confirmLogModalVisible}
        footer={null}
        onCancel={() => setConfirmLogModalVisible(false)}
        width={900}
      >
        {confirmLogRecord && (
          <div style={{ marginBottom: 20, }}>
            <div style={{ marginBottom: 16, display: 'flex', gap: 16, alignItems: 'center' }}>
              <span>当前状态: {renderReviewStatusTag(confirmLogRecord.status)}</span>
              <span>出款金额: <Text strong>{formatAmountZhCn(confirmLogRecord.paidAmount)}</Text></span>
              <span>出款人: <Text strong>{confirmLogRecord.paidUserName}</Text></span>
              <span>出款日期: <Text strong>{confirmLogRecord.paidDate}</Text></span>
            </div>
            <Table
              rowKey="id"
              size="small"
              dataSource={confirmLogs}
              pagination={false}
              columns={[
                {
                  title: "操作时间",
                  dataIndex: "createdAt",
                  width: 160,
                },
                {
                  title: "操作类型",
                  dataIndex: "action",
                  width: 120,
                  render: (value: any) => {
                    if (value === "SUBMIT") return <Tag color="blue" size="small" shape="circle">提交</Tag>;
                    if (value === "CONFIRM") return <Tag color="green" size="small" shape="circle">确认</Tag>;
                    if (value === "REJECT") return <Tag color="red" size="small" shape="circle">驳回</Tag>;
                    if (value === "AUTO_CONFIRM") return <Tag color="cyan" size="small" shape="circle">自动确认</Tag>;
                    if (value === "RESUBMIT") return <Tag color="purple" size="small" shape="circle">重新提交</Tag>;
                    if (value === "CANCEL") return <Tag color="cyan" size="small" shape="circle">取消</Tag>;
                    if (value === "CANCEL_CONFIRM") return <Tag color="teal" size="small" shape="circle">取消确认</Tag>;
                    if (value === "EDIT") return <Tag color="purple" size="small" shape="circle">编辑</Tag>;
                    if (value === "EDIT_CONFIRM") return <Tag color="violet" size="small" shape="circle">编辑确认</Tag>;
                    if (value === "WITHDRAW") return <Tag color="grey" size="small" shape="circle">撤销提交</Tag>;
                    return <Tag size="small">{value}</Tag>;
                  },
                },
                 {
                  title: "状态变化",
                  width: 140,
                  render: (_: any, record: any) => renderStatusTransition(record.previousStatus, record.confirmStatus),
                },
                {
                  title: "操作说明",
                  dataIndex: "paidOptType",
                  width: 80,
                  render: (value: any) => {
                    if (value === "HK") return "还款";
                    if (value === "JQ") return "结清";
                    if (value === "GZ") return "过账";
                    return value || '-';
                  },
                },
                {
                  title: "操作人",
                  dataIndex: "operatorName",
                  width: 100,
                },
                {
                  title: "出款人",
                  dataIndex: "paidUserName",
                  width: 100,
                  render: (value: any) => value || '-',
                },
               
                {
                  title: "备注",
                  dataIndex: "remark",
                  render: (value: any) => {
                    if (!value) return '-';
                    return value.replace(/\[prev:\w+\]\s*/g, '') || '-';
                  },
                },
              ]}
              bordered
            />
          </div>
        )}
      </Modal>

      {/* ====== 三Tab页面 ====== */}
      <Tabs type="line" onChange={(e) => onTabSelect(e)} style={{ margin: 10 }}>
        {/* Tab1: 信贷客户 (loanType=MORTGAGE)
            entityKey="repaymentPlan" → GET /repaymentPlan?loanType=MORTGAGE
            统计: queryStatCount → GET /repaymentPlan/listSum
            列定义: cols (通用列) */}
        <TabPane
          tab={<span><IconMoneyExchangeStroked /> 信贷客户 </span>}
          itemKey="1"
        >
          <Row>
            <RowCol span={24}>{listSumStat()}</RowCol>
          </Row>
          <DueDateQuickButtons apiRef={crudApiRef} formRef={filterFormRef} loanType="MORTGAGE" refreshKey={calendarRefreshKey} active={currentTab === '1'} />
          <ConfirmStatusFilterButtons formRef={filterFormRef} confirmStatusCounts={confirmStatusCounts} currentTab={currentTab} crudApi={crudApi} crudApiCard={crudApiCard} crudApiBorrow={crudApiBorrow} />
          <Crud
            getCrudApi={(api) => { setCrudApi(api); crudApiRef.current = api; }}
            getFilterFormApi={(_crudApi, formApi) => { filterFormRef.current = formApi; }}
            initFilterValues={initFilterValues}
            params={{ ...initParams, loanType: "MORTGAGE" }}
            canUpdate={false}
            canCreate={false}
            canDelete={false}
            select={true}
            actions={batchActions}
            name="还款计划"
            entityKey="repaymentPlan"
            pageSize={50}
            rowActionWidth={0}
            fetchDataAfterMethod={(data: any) => { queryStatCount(data); queryConfirmStatusCount(data); }}
            cols={cols}
          />
        </TabPane>

        {/* Tab2: 信用卡客户 (loanType=CARD)
            entityKey="repaymentPlan" → GET /repaymentPlan?loanType=CARD
            统计: queryStatCountCard → GET /repaymentPlan/listSum
            列定义: cardCols (通用列 + 账单日 + 过账金额)
            特有操作: "过账"按钮 */}
        <TabPane
          tab={<span><IconIdCard /> 信用卡客户 </span>}
          itemKey="2"
        >
          <Row>
            <RowCol span={24}>{listSumStatCard()}</RowCol>
          </Row>
          <DueDateQuickButtons apiRef={crudApiCardRef} formRef={filterFormCardRef} loanType="CARD" refreshKey={calendarRefreshKey} active={currentTab === '2'} />
          <ConfirmStatusFilterButtons formRef={filterFormCardRef} confirmStatusCounts={confirmStatusCountsCard} currentTab={currentTab} crudApi={crudApi} crudApiCard={crudApiCard} crudApiBorrow={crudApiBorrow} />
          <Crud
            getCrudApi={(api) => { setCrudApiCard(api); crudApiCardRef.current = api; }}
            getFilterFormApi={(_crudApi, formApi) => { filterFormCardRef.current = formApi; }}
            initFilterValues={initFilterValues}
            params={{ ...initParams, loanType: "CARD" }}
            canUpdate={false}
            canCreate={false}
            canDelete={false}
            select={true}
            actions={batchActions}
            name="还款计划"
            entityKey="repaymentPlan"
            pageSize={50}
            rowActionWidth={0}
            fetchDataAfterMethod={(data: any) => { queryStatCountCard(data); queryConfirmStatusCountCard(data); }}
            cols={cardCols}
          />
        </TabPane>

        {/* Tab3: 个人借款 (loanType=BORROW)
            entityKey="repaymentPlan" → GET /repaymentPlan?loanType=BORROW
            统计: queryStatCountBorrow → GET /repaymentPlan/listSum
            列定义: borrowCols (出款人→出借人, 增加 出借日期, 无 过账/账单日) */}
        <TabPane
          tab={<span><IconCoinMoneyStroked /> 个人借款 </span>}
          itemKey="3"
        >
          <Row>
            <RowCol span={24}>{listSumStatBorrow()}</RowCol>
          </Row>
          <DueDateQuickButtons apiRef={crudApiBorrowRef} formRef={filterFormBorrowRef} loanType="BORROW" refreshKey={calendarRefreshKey} active={currentTab === '3'} />
          <ConfirmStatusFilterButtons formRef={filterFormBorrowRef} confirmStatusCounts={confirmStatusCountsBorrow} currentTab={currentTab} crudApi={crudApi} crudApiCard={crudApiCard} crudApiBorrow={crudApiBorrow} />
          <Crud
            getCrudApi={(api) => { setCrudApiBorrow(api); crudApiBorrowRef.current = api; }}
            getFilterFormApi={(_crudApi, formApi) => { filterFormBorrowRef.current = formApi; }}
            initFilterValues={initFilterValues}
            params={{ ...initParams, loanType: "BORROW" }}
            canUpdate={false}
            canCreate={false}
            canDelete={false}
            select={true}
            actions={batchActions}
            name="还款计划"
            entityKey="repaymentPlan"
            pageSize={50}
            rowActionWidth={0}
            fetchDataAfterMethod={(data: any) => { queryStatCountBorrow(data); queryConfirmStatusCountBorrow(data); }}
            cols={borrowCols}
          />
        </TabPane>
      </Tabs>
    </>
  );
}
